import React, { useState, useEffect } from "react";
import useApiEp from "../../hooks/useApiEp";
import { Alert, Container, Box } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import PageTitle from "../../widgets/PageTitle";
import DataTable from "../../widgets/DataTable";
import { getErrorResponse, roundToMaxDecimals } from "../../utils/util";
import MuPb from "../../widgets/MuPb";
import CreditStats from "./CreditStats";

const CreditHistory = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("recharge");

  const { data, isLoading, isError, error } = useApiEp(
    `/credit/get-recharge-history?page=${page}&limit=${limit}&customerId=${selectedCustomer}&type=${selectedTransaction}&rechargeType=${selectedType}&category=${selectedCategory}`
  );

  const {
    data: customerIds,
    isLoading: isCLoading,
    isError: isCerror,
    error: cError,
  } = useApiEp(`/credit/customer-ids`);

  const {
    data: rTypes,
    isLoading: isRtypeLoading,
    isError: isRerror,
    error: rError,
  } = useApiEp(`/credit/get-unique-recharge-types`);

  const TRANSACTIONS = ["credit", "debit"];
  const CATEGORIES = ["usage", "recharge"];

  useEffect(() => {
    setPage(1);
  }, [selectedCustomer, selectedType, selectedTransaction, selectedCategory]);

  return (
    <Container sx={{ mt: 3 }}>
      <CreditStats />
      <PageTitle my={2}>Credit History</PageTitle>

      {/* Filters - Always rendered */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Autocomplete
          options={customerIds ?? []}
          value={selectedCustomer || null}
          onChange={(event, newValue) => setSelectedCustomer(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Customer ID"
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isCLoading ? <div>Loading...</div> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          clearOnBlur={false}
          handleHomeEndKeys
          disabled={isCerror}
        />

        <Autocomplete
          options={rTypes ?? []}
          value={selectedType || null}
          onChange={(event, newValue) => setSelectedType(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Recharge Type"
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isRtypeLoading ? <div>Loading...</div> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          clearOnBlur={false}
          handleHomeEndKeys
          disabled={isRerror}
        />

        <Autocomplete
          options={["", ...TRANSACTIONS]}
          value={selectedTransaction || ""}
          onChange={(event, newValue) => setSelectedTransaction(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Transaction Type"
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          renderOption={(props, option) => (
            <li {...props}>
              {option === "" ? "All" : option === "credit" ? "Credit" : "Debit"}
            </li>
          )}
          clearOnBlur={false}
          handleHomeEndKeys
        />

        <Autocomplete
          options={["", ...CATEGORIES]}
          value={selectedCategory}
          onChange={(event, newValue) => setSelectedCategory(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          renderOption={(props, option) => (
            <li {...props}>
              {option === ""
                ? "All"
                : option === "usage"
                ? "Usage"
                : "Recharge"}
            </li>
          )}
          clearOnBlur={false}
          handleHomeEndKeys
        />
      </Box>

      {/* Error alerts for filter data */}
      {isCerror && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load customer IDs: {getErrorResponse(cError)}
        </Alert>
      )}
      {isRerror && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load recharge types: {getErrorResponse(rError)}
        </Alert>
      )}

      {/* Main data table with conditional rendering */}
      {isError ? (
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      ) : (
        <DataTable
          sx={{ my: 1 }}
          columns={[
            {
              id: "serial",
              label: "S.No.",
              render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
            },
            {
              id: "type",
              label: "Type",
              render: (v) => (
                <span style={{ color: v === "credit" ? "green" : "red" }}>
                  {v === "credit" ? "Credit" : "Debit"}
                </span>
              ),
            },
            { id: "customerId", label: "Customer ID" },
            {
              id: "prevous",
              label: "Previous",
              render: (v) => roundToMaxDecimals(v, 2),
            },
            {
              id: "amount",
              label: "Amount",
              render: (v) => roundToMaxDecimals(v, 2),
            },
            { id: "remarks", label: "Remarks" },
            {
              id: "updatedOn",
              label: "Date",
              render: (v) =>
                new Date(v).toLocaleString("en-IN", {
                  dateStyle: "medium",
                }),
            },
          ]}
          actions={[]}
          rows={data?.data ?? []}
          page={page}
          limit={limit}
          totalPages={data?.totalPages ?? 1}
          onPageChange={setPage}
          loading={isLoading}
        />
      )}
    </Container>
  );
};

export default CreditHistory;
